import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, ArrowRight, CheckCircle, Clock, Shield, Star, MapPin,
  AlertTriangle, Zap, Award, ThumbsUp
} from 'lucide-react';
import { companyInfo, services, cities, testimonials } from '../../data/mock';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import SEO, { seoData, getLocalBusinessSchema, getFAQSchema } from '../seo/SEO';
import { FAQSection, InternalLinksSection, faqVitrerieVilliers } from '../seo/FAQSection';

const HomePage = () => {
  const vitrerie = services.find(s => s.id === 'vitrerie');

  return (
    <div className="min-h-screen">
      {/* SEO Meta Tags */}
      <SEO 
        title={seoData.home.title}
        description={seoData.home.description}
        keywords={seoData.home.keywords}
        canonical="/"
        schemaData={getLocalBusinessSchema()}
        additionalSchemas={[getFAQSchema()]}
      />

      {/* Hero Section - Optimisé Vitrier Villiers-sur-Marne */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.pexels.com/photos/5691503/pexels-photo-5691503.jpeg?auto=compress&cs=tinysrgb&w=1920)' 
          }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <Badge className="badge-emergency mb-6 animate-fade-in">
              <Clock className="w-3 h-3 mr-1" /> Vitrier Urgence 24h/24 – 7j/7
            </Badge>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 animate-fade-in-up">
              <span className="text-[hsl(24,95%,53%)]">Vitrier</span> à Villiers-sur-Marne
            </h1>
            
            <p className="text-xl text-gray-200 mb-4 animate-fade-in-up delay-100">
              <strong>Remplacement de vitre, bris de glace, double vitrage</strong>
            </p>
            <p className="text-lg text-gray-300 mb-8 animate-fade-in-up delay-100">
              Vitrier miroitier qualifié à Villiers-sur-Marne 94350, Val-de-Marne. 
              Notre savoir-faire en vitrerie : intervention rapide en moins d'1 heure. 
              Dépannage vitrerie urgence 24h/24 dans tout le Val-de-Marne et Île-de-France.
            </p>

            <div className="flex flex-wrap gap-4 mb-10 animate-fade-in-up delay-200">
              <a href={`tel:${companyInfo.phoneEmergency}`}>
                <Button size="lg" className="emergency-btn rounded-full text-lg px-8 py-6">
                  <Phone className="w-5 h-5 mr-2" />
                  Urgence Vitrier : {companyInfo.phoneEmergency}
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" className="bg-white text-[hsl(207,78%,35%)] hover:bg-gray-100 rounded-full text-lg px-8 py-6">
                  Devis gratuit
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap items-center gap-6 text-white/90 animate-fade-in-up delay-300">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[hsl(24,95%,53%)]" />
                <span>Devis gratuit</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-[hsl(24,95%,53%)]" />
                <span>Intervention -1h</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[hsl(24,95%,53%)]" />
                <span>Garantie travaux</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[hsl(24,95%,53%)]" />
                <span>24h/24 - 7j/7</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/60 rounded-full" />
          </div>
        </div>
      </section>

      {/* Urgence Vitrerie Section */}
      <section className="py-12 bg-[hsl(24,95%,53%)] text-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <AlertTriangle className="w-12 h-12" />
              <div>
                <h2 className="text-2xl font-bold">Vitres cassées ? Bris de glace ?</h2>
                <p className="text-white/90">Vitrier à Villiers-sur-Marne 94350 - Dépannage vitrerie immédiat</p>
              </div>
            </div>
            <a href={`tel:${companyInfo.phoneEmergency}`}>
              <Button size="lg" className="bg-white text-[hsl(24,95%,53%)] hover:bg-gray-100 rounded-full text-xl px-10 py-6 font-bold">
                <Phone className="w-6 h-6 mr-3" />
                {companyInfo.phoneEmergency}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Section Rénovation avec Paiement Facilité - SEO optimisé */}
      <section className="py-12 bg-gradient-to-r from-[hsl(207,78%,42%)] to-[hsl(207,78%,35%)] text-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="flex-1">
              <Badge className="bg-white/20 text-white mb-3">
                <Clock className="w-3 h-3 mr-1" /> Paiement en plusieurs fois
              </Badge>
              <h2 className="text-2xl md:text-3xl font-bold mb-3">
                Rénovation fenêtres à Villiers-sur-Marne
              </h2>
              <p className="text-white/90 mb-2">
                <strong>Fenêtres PVC, aluminium, bois</strong> – Pose sur mesure dans le Val-de-Marne (94)
              </p>
              <p className="text-white/80 text-sm">
                Étalez le coût de vos travaux sur 3 à 24 mois. Devis gratuit et simulation de financement.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/renovation-fenetres-paiement-plusieurs-fois-villiers-sur-marne">
                <Button size="lg" className="bg-white text-[hsl(207,78%,35%)] hover:bg-gray-100 rounded-full font-bold px-6">
                  <ArrowRight className="w-5 h-5 mr-2" />
                  Simuler mon financement
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-6">
                  Devis gratuit rénovation
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Vitrerie Section */}
      <section className="py-20 bg-[hsl(210,20%,98%)]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <Badge className="badge-primary mb-4">Vitrerie à Villiers-sur-Marne</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-[hsl(215,25%,15%)] mb-4">
              Vitrier Miroitier - Nos prestations vitrerie
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Vitrier à Villiers-sur-Marne 94350, Val-de-Marne. Spécialiste du remplacement de vitre, 
              bris de glace, double vitrage, baie vitrée. Intervention Champigny-sur-Marne, 
              Bry-sur-Marne, Nogent-sur-Marne et toute l'Île-de-France.
            </p>
            <div className="section-divider" />
          </div>

          {/* Services vitrerie en premier */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Vitrerie en premier - mise en avant */}
            <Link to="/services/vitrerie" className="md:col-span-2 lg:col-span-1">
              <Card className="service-card h-full cursor-pointer border-2 border-[hsl(24,95%,53%)] shadow-lg hover:shadow-xl">
                <div className="relative h-48 overflow-hidden rounded-t-lg">
                  <img 
                    src={vitrerie?.image} 
                    alt="Vitrier Villiers-sur-Marne - Remplacement vitre cassée"
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                    loading="eager"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-[hsl(24,95%,53%)] text-white">Service prioritaire</Badge>
                  </div>
                  <div className="absolute bottom-4 left-4">
                    <h3 className="text-xl font-bold text-white">Vitrier - Vitrerie</h3>
                  </div>
                </div>
                <CardContent className="p-5">
                  <p className="text-muted-foreground text-sm mb-4">
                    <strong>Vitrier urgence Villiers-sur-Marne</strong> - Remplacement vitre cassée, 
                    bris de glace, double vitrage. Intervention 24h/24.
                  </p>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-[hsl(24,95%,53%)]" />
                      <span>Remplacement vitre cassée urgence</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-[hsl(24,95%,53%)]" />
                      <span>Bris de glace 24h/24</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-[hsl(24,95%,53%)]" />
                      <span>Double vitrage isolation</span>
                    </li>
                  </ul>
                  <div className="flex items-center text-[hsl(24,95%,53%)] font-semibold text-sm">
                    Voir nos services vitrerie <ArrowRight className="w-4 h-4 ml-1" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            {/* Autres services */}
            {services.filter(s => s.id !== 'vitrerie').map((service) => (
              <Link key={service.id} to={`/services/${service.id}`}>
                <Card className="service-card h-full cursor-pointer border-0 shadow-md hover:shadow-xl">
                  <div className="relative h-48 overflow-hidden rounded-t-lg">
                    <img 
                      src={service.image} 
                      alt={`${service.title} Villiers-sur-Marne`}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <h3 className="text-xl font-bold text-white">{service.title}</h3>
                    </div>
                  </div>
                  <CardContent className="p-5">
                    <p className="text-muted-foreground text-sm mb-4">
                      {service.shortDesc}
                    </p>
                    <ul className="space-y-2 mb-4">
                      {service.features.slice(0, 3).map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-[hsl(207,78%,42%)]" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-center text-[hsl(207,78%,42%)] font-medium text-sm">
                      En savoir plus <ArrowRight className="w-4 h-4 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Pourquoi choisir NGL SERVICES */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="badge-primary mb-4">Vitrier miroitier qualifié</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-[hsl(215,25%,15%)] mb-6">
                Pourquoi choisir NGL SERVICES pour votre{' '}
                <span className="text-[hsl(207,78%,42%)]">vitrerie à Villiers-sur-Marne</span> ?
              </h2>
              <p className="text-muted-foreground mb-8">
                NGL SERVICES, votre vitrier miroitier de confiance à Villiers-sur-Marne 94350. 
                Nous intervenons en urgence pour tout remplacement de vitre, vitres cassées, 
                bris de glace et installation de double vitrage. Notre savoir-faire reconnu 
                dans le Val-de-Marne : Champigny-sur-Marne, Bry-sur-Marne, Nogent-sur-Marne.
              </p>

              <div className="space-y-6">
                {[
                  { icon: Zap, title: "Intervention rapide", desc: "Vitrier sur place en moins d'1 heure à Villiers-sur-Marne et Val-de-Marne" },
                  { icon: Clock, title: "Urgence 24h/24", desc: "Dépannage vitrerie disponible jour et nuit, 7j/7" },
                  { icon: Award, title: "Savoir-faire reconnu", desc: "Vitrier miroitier expérimenté pour tous types de vitrages et baies vitrées" },
                  { icon: ThumbsUp, title: "Devis gratuit", desc: "Estimation transparente avant toute intervention vitrerie" }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="service-icon flex-shrink-0">
                      <item.icon className="w-7 h-7 text-[hsl(207,78%,42%)]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-1">{item.title}</h3>
                      <p className="text-muted-foreground text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 flex flex-wrap gap-4">
                <a href={`tel:${companyInfo.phoneEmergency}`}>
                  <Button className="emergency-btn rounded-full px-8">
                    <Phone className="w-4 h-4 mr-2" />
                    Appeler le vitrier
                  </Button>
                </a>
                <Link to="/contact">
                  <Button className="btn-primary rounded-full px-8">
                    Devis gratuit
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/323776/pexels-photo-323776.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Vitrier Villiers-sur-Marne - Remplacement double vitrage"
                className="rounded-2xl shadow-2xl"
              />
              {/* Floating card */}
              <div className="absolute -bottom-8 -left-8 bg-white rounded-xl shadow-xl p-6 max-w-xs hidden md:block">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-12 h-12 rounded-full bg-[hsl(24,95%,53%)] flex items-center justify-center">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Vitrier urgence 24h/24</p>
                    <p className="font-bold text-[hsl(215,25%,15%)]">{companyInfo.phoneEmergency}</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Vitre cassée ? Bris de glace ? Intervention immédiate à Villiers-sur-Marne
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Témoignages */}
      <section className="py-20 bg-[hsl(207,78%,97%)]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <Badge className="badge-primary mb-4">Avis clients vitrier</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-[hsl(215,25%,15%)] mb-4">
              Ce que nos clients disent de notre service vitrerie
            </h2>
            <div className="section-divider" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="testimonial-card border-0 shadow-lg">
                <CardContent className="p-6 pt-10">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-[hsl(24,95%,53%)] text-[hsl(24,95%,53%)]" />
                    ))}
                  </div>
                  <p className="text-muted-foreground mb-6 italic text-sm">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-[hsl(215,25%,15%)]">{testimonial.name}</p>
                      <p className="text-sm text-muted-foreground">{testimonial.city}</p>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {testimonial.service}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Zones d'intervention */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <Badge className="badge-primary mb-4">Vitrier proximité</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-[hsl(215,25%,15%)] mb-4">
              Vitrier Villiers-sur-Marne et environs
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              NGL SERVICES, votre vitrier à Villiers-sur-Marne, intervient également dans toutes les communes du Val-de-Marne.
            </p>
            <div className="section-divider" />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {cities.map((city) => (
              <Link key={city.id} to={`/villes/${city.id}`}>
                <div className={`city-card text-center ${
                  city.isPrimary ? 'bg-[hsl(207,78%,95%)] border-[hsl(207,78%,42%)]' : ''
                }`}>
                  <MapPin className={`w-5 h-5 mx-auto mb-2 ${
                    city.isPrimary ? 'text-[hsl(207,78%,42%)]' : 'text-gray-400'
                  }`} />
                  <p className={`font-medium text-sm ${
                    city.isPrimary ? 'text-[hsl(207,78%,42%)]' : 'text-[hsl(215,25%,15%)]'
                  }`}>
                    {city.name}
                  </p>
                  {city.isPrimary && (
                    <span className="text-xs text-[hsl(24,95%,53%)]">(Siège)</span>
                  )}
                </div>
              </Link>
            ))}
          </div>

          {/* Liens internes SEO */}
          <div className="mt-12 p-6 bg-[hsl(210,20%,98%)] rounded-xl">
            <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-4">Services vitrerie par ville :</h3>
            <div className="flex flex-wrap gap-3">
              <Link to="/vitrier-villiers-sur-marne" className="text-[hsl(207,78%,42%)] hover:underline text-sm">
                Vitrier Villiers-sur-Marne
              </Link>
              <span className="text-gray-300">|</span>
              <Link to="/vitrier-urgence-villiers-sur-marne" className="text-[hsl(207,78%,42%)] hover:underline text-sm">
                Vitrier urgence Villiers-sur-Marne
              </Link>
              <span className="text-gray-300">|</span>
              <Link to="/remplacement-vitre-cassee-villiers-sur-marne" className="text-[hsl(207,78%,42%)] hover:underline text-sm">
                Remplacement vitre cassée
              </Link>
              <span className="text-gray-300">|</span>
              <Link to="/bris-de-glace-villiers-sur-marne" className="text-[hsl(207,78%,42%)] hover:underline text-sm">
                Bris de glace Villiers-sur-Marne
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - Schema FAQPage pour SEO et IA */}
      <FAQSection 
        faqs={faqVitrerieVilliers.slice(0, 6)} 
        title="Questions fréquentes - Vitrier Villiers-sur-Marne"
        subtitle="Trouvez les réponses à vos questions sur nos services de vitrerie à Villiers-sur-Marne et Val-de-Marne"
      />

      {/* Maillage interne renforcé */}
      <InternalLinksSection currentPage="/" />

      {/* CTA Section */}
      <section className="py-20 bg-[hsl(215,25%,15%)] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
          }} />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Besoin d'un vitrier à Villiers-sur-Marne ?
            </h2>
            <p className="text-gray-300 text-lg mb-8">
              Vitre cassée, bris de glace, remplacement double vitrage... 
              Nos artisans vitriers interviennent en urgence 24h/24 à Villiers-sur-Marne.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href={`tel:${companyInfo.phoneEmergency}`}>
                <Button size="lg" className="emergency-btn rounded-full text-lg px-8">
                  <Phone className="w-5 h-5 mr-2" />
                  {companyInfo.phoneEmergency}
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" className="bg-white text-[hsl(207,78%,35%)] hover:bg-gray-100 rounded-full text-lg px-8">
                  Devis gratuit
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
